//teamsService.js

import { DayOfWeek, Team, TeamCreatePayload } from '@/types/team';
import { HttpClient } from './HttpClient';

const API_URL = '/team';

export interface TeamsResponse {
    data: Team[];
}

export interface TeamResponse {
    data: Team
}

function mapTeamFromBackend(data: any): Team {
    console.log("data", data);

    return {
        id: data.id,
        name: data.name,
        icon: data.icon,
        teamMembers: data.members,
        routingStrategy: data.routing_strategy,
        officeHours: data.office_hours || [],
        holidays: data.holidays || [],
        channels: data.channels,
        workspaceId: data.workspace_id,
        createdAt: data.created_at,
        updatedAt: data.updated_at
    }
}


const teamsService = {
    //get teams
    async fetchTeams(): Promise<TeamsResponse> {
        try {
            const response = await HttpClient.apiClient.get(API_URL);

            // Handle the case where API returns success but no data
            if (response.data.status === 'success') {
                const teamsData = response.data.data || [];
                return {
                    data: Array.isArray(teamsData) ? teamsData : []
                };
            }

            return response.data;
        } catch (error) {
            console.error('Error fetching teams:', error);
            throw new Error('Failed to fetch teams');
        }
    },
    //get team by id
    async getTeamById(id: string): Promise<TeamResponse> {
        try {
            const response = await HttpClient.apiClient.get<TeamResponse>(`${API_URL}/${id}`);
            return response.data;
        } catch (error) {
            console.error('Error fetching team by id:', error);
            throw new Error('Failed to fetch team by id');
        }
    },

    async createTeam(team: TeamCreatePayload): Promise<TeamResponse> {
        try {
            // Format the payload according to the backend expectation
            const payload = {
                name: team.name,
                icon: team.icon,
                members: team.members,
                channels: team.channels,
                routingStrategy: team.routing.type,
                ...(team.routing.limits?.maxTotalTickets !== undefined && { maxTotalTickets: team.routing.limits.maxTotalTickets }),
                ...(team.routing.limits?.maxOpenTickets !== undefined && { maxOpenTickets: team.routing.limits.maxOpenTickets }),
                ...(team.routing.limits?.maxActiveChats !== undefined && { maxActiveChats: team.routing.limits.maxActiveChats }),
                officeHours: team.officeHours,
                holidays: team.holidays,
            };

            const response = await HttpClient.apiClient.post<{ data: any }>(API_URL, payload);
            return { data: mapTeamFromBackend(response.data.data) };
        } catch (error) {
            console.error('Error creating team:', error);
            throw new Error('Failed to create team');
        }
    },

    async updateTeam(id: string, team: Partial<TeamCreatePayload>): Promise<TeamResponse> {
        try {
            // Format the payload according to the backend expectation
            const payload: any = {
            };

            if (team.name) payload.name = team.name;
            if (team.icon !== undefined) payload.icon = team.icon;
            if (team.members) payload.members = team.members;

            // Handle channels properly
            if (team.channels) {
                payload.channels = {
                    chat: team.channels.chat,
                    email: Array.isArray(team.channels.email) ? team.channels.email : []
                };
            }

            // Handle routingStrategy and limits
            if (team.routing) {
                payload.routingStrategy = team.routing.type;

                if (team.routing.limits) {
                    if (team.routing.limits.maxTotalTickets !== undefined) {
                        payload.maxTotalTickets = team.routing.limits.maxTotalTickets;
                    }
                    if (team.routing.limits.maxOpenTickets !== undefined) {
                        payload.maxOpenTickets = team.routing.limits.maxOpenTickets;
                    }
                    if (team.routing.limits.maxActiveChats !== undefined) {
                        payload.maxActiveChats = team.routing.limits.maxActiveChats;
                    }
                }
            }

            // Handle officeHours and holidays safely
            if (team.officeHours) {
                // Ensure all days exist in officeHours
                const defaultDays = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];
                const safeOfficeHours: any = {};

                defaultDays.forEach(day => {
                    safeOfficeHours[day] = Array.isArray(team.officeHours?.[day as DayOfWeek])
                        ? team.officeHours[day as DayOfWeek]
                        : [];
                });

                payload.officeHours = safeOfficeHours;
            }

            if (team.holidays !== undefined) {
                payload.holidays = Array.isArray(team.holidays) ? team.holidays : [];
            }

            console.log('Sending team update payload:', payload);
            const response = await HttpClient.apiClient.put<{ data: any }>(`${API_URL}/${id}`, payload);
            console.log('Team update response:', response.data);
            return { data: mapTeamFromBackend(response.data.data) };
        } catch (error) {
            console.error('Error updating team:', error);
            throw new Error('Failed to update team');
        }
    },

    async deleteTeam(id: string): Promise<void> {
        try {
            await HttpClient.apiClient.delete(`${API_URL}/${id}`);
        } catch (error) {
            console.error('Error deleting team:', error);
            throw new Error('Failed to delete team');
        }
    },

    async getTeam(id: string): Promise<TeamResponse> {
        try {
            const response = await HttpClient.apiClient.get<{ data: any }>(`${API_URL}/${id}`);
            return { data: mapTeamFromBackend(response.data.data) };
        } catch (error) {
            console.error('Error fetching team:', error);
            throw new Error('Failed to fetch team details');
        }
    }
};

export default teamsService;
